/**
 * App binding for the shared chunk-aware state machine
 * (`@listening/frontend/audio-chunk-player/core`). The state machine itself is
 * platform-agnostic; this wrapper injects start's app-specific seams
 * (session-derived auth gate, doc-pack kill-switch flag) and re-exports
 * the shared surface so existing `@/lib/audio-chunk-player` import paths keep
 * working.
 */

import { AudioChunkPlayerProviderInternal as SharedAudioChunkPlayerProviderInternal } from '@listening/frontend/audio-chunk-player/core';
import type { PropsWithChildren } from 'react';
import { useDocPackLoadingEnabled } from '@/lib/doc-reader/pack/doc-pack-source';
import { useIsUserLoggedIn } from '@/services/user-service';

export {
  type AudioChunkPlayerState,
  getDocPackPlaybackAnchorForPosition,
  type DocPackPlaybackAnchor,
  useCurrentAudioChunkQuery,
  useDocumentSummary,
  usePreferedVoice,
} from '@listening/frontend/audio-chunk-player/core';

export function AudioChunkPlayerProviderInternal({
  children,
  ...props
}: PropsWithChildren & {
  delayToPrefetchMs?: number;
  initialAudioId?: string;
  initialLookupAudioId?: string;
}) {
  const userLoggedIn = !!useIsUserLoggedIn();
  // Same kill-switch source as every other web pack consumer (item #7 of
  // review-issues-1.md): the engine gates its doc-pack path on this AND the
  // pack version check.
  const docPackEnabled = useDocPackLoadingEnabled();

  return (
    <SharedAudioChunkPlayerProviderInternal
      {...props}
      userLoggedIn={userLoggedIn}
      docPackEnabled={docPackEnabled}
    >
      {children}
    </SharedAudioChunkPlayerProviderInternal>
  );
}
